/**
 * SupervisorOverview — the lexsup runs + findings console (lexsup plan §3.7).
 *
 * Two read-only panels over the lexserver `/api/platform/sup/*` proxy:
 *   - Recent runs   (`/runs`)     — one row per fleet-audit execution, with
 *                                   trigger, status, findings count, LLM cost.
 *   - Recent findings (`/findings`) — severity-colored observation list, with a
 *                                   severity filter.
 *
 * Mirrors the PlatformHealth / RuntimeFleet conventions (Card layout, grey
 * "nothing yet" empties, red error banner). The landing page is
 * SupervisorMonitor (health + launchpad); this is the data drill-down it links
 * to.
 */

import { AlertTriangle, ListChecks, Loader2, ScrollText } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { usePlatformApi } from "../hooks/usePlatformApi";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import type {
  SupervisorFinding,
  SupervisorFindingSeverity,
  SupervisorRun,
} from "../types/supervisor";

const SEVERITIES: SupervisorFindingSeverity[] = [
  "info",
  "low",
  "medium",
  "high",
  "critical",
];

/** Severity → badge classes (Tailwind, dark-aware) — low-key for info, loud
 *  for critical. */
const SEVERITY_BADGE: Record<SupervisorFindingSeverity, string> = {
  info: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  low: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  medium: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  high: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  critical: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
};

function fmtTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function fmtCost(usd?: number): string {
  if (usd == null) return "—";
  return `$${usd.toFixed(4)}`;
}

export function SupervisorOverview() {
  const api = usePlatformApi();
  const [runs, setRuns] = useState<SupervisorRun[]>([]);
  const [findings, setFindings] = useState<SupervisorFinding[]>([]);
  const [severity, setSeverity] = useState<SupervisorFindingSeverity | "">("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [runsRes, findingsRes] = await Promise.all([
        api.listSupervisorRuns({ limit: 50 }),
        api.listSupervisorFindings({
          limit: 100,
          severity: severity || undefined,
        }),
      ]);
      setRuns(runsRes.data.records);
      setFindings(findingsRes.data.records);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load supervisor data");
    } finally {
      setLoading(false);
    }
  }, [api, severity]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid="supervisor-overview-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <ListChecks className="h-5 w-5 text-indigo-500" /> Supervisor Runs &amp;
            Findings
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Recent fleet-audit runs and the findings they produced.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div
          className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
          data-testid="supervisor-overview-error"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Recent runs */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <ScrollText className="h-4 w-4" /> Recent runs
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          {runs.length === 0 ? (
            <p
              className="py-10 text-center text-sm text-slate-400"
              data-testid="supervisor-runs-empty"
            >
              {loading ? "Loading runs…" : "No supervisor runs recorded yet."}
            </p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
                  <th className="px-4 py-2.5">Started</th>
                  <th className="px-4 py-2.5">Kind</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Triggered by</th>
                  <th className="px-4 py-2.5 text-right">Findings</th>
                  <th className="px-4 py-2.5">Model</th>
                  <th className="px-4 py-2.5 text-right">Cost</th>
                  <th className="px-4 py-2.5">Dry-run</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr
                    key={r.run_id}
                    className="border-b border-slate-100 last:border-0 dark:border-slate-800"
                    data-testid={`supervisor-run-${r.run_id}`}
                  >
                    <td className="px-4 py-2.5 text-xs">{fmtTime(r.started_at)}</td>
                    <td className="px-4 py-2.5 text-xs">{r.kind}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className={
                          r.status === "completed"
                            ? "text-xs font-medium text-green-600 dark:text-green-400"
                            : "text-xs font-medium text-red-600 dark:text-red-400"
                        }
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">
                      {r.triggered_by ?? "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {r.findings_count}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">
                      {r.llm ? `${r.llm.provider}/${r.llm.model}` : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs tabular-nums">
                      {fmtCost(r.llm?.cost_usd)}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {r.dry_run ? (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                          dry-run
                        </span>
                      ) : (
                        <span className="text-slate-400">live</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Recent findings */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <ListChecks className="h-4 w-4" /> Recent findings
          </CardTitle>
          <select
            value={severity}
            onChange={(e) =>
              setSeverity(e.target.value as SupervisorFindingSeverity | "")
            }
            data-testid="supervisor-findings-severity"
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
          >
            <option value="">All severities</option>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </CardHeader>
        <CardContent className="p-0">
          {findings.length === 0 ? (
            <p
              className="py-10 text-center text-sm text-slate-400"
              data-testid="supervisor-findings-empty"
            >
              {loading
                ? "Loading findings…"
                : severity
                  ? `No ${severity} findings.`
                  : "No supervisor findings recorded yet."}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {findings.map((f, i) => (
                <li
                  key={`${f.run_id}-${i}`}
                  className="flex items-start gap-3 px-4 py-3"
                  data-testid="supervisor-finding-row"
                >
                  <span
                    className={`mt-0.5 inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${SEVERITY_BADGE[f.severity] ?? SEVERITY_BADGE.info}`}
                  >
                    {f.severity}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">{f.summary}</p>
                    {f.detail && (
                      <p className="mt-0.5 text-xs text-muted-foreground">{f.detail}</p>
                    )}
                    <p className="mt-1 text-[11px] text-slate-400">
                      <span className="font-mono">{f.subsystem}</span> &middot;{" "}
                      {fmtTime(f.created_at)}
                      {f.trace_id && (
                        <>
                          {" "}
                          &middot; trace{" "}
                          <span className="font-mono">{f.trace_id.slice(0, 12)}</span>
                        </>
                      )}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
